import { avatarName, avatarPicture, type AvatarKey } from '@hazel/shared'

import { fallBackToDrawing } from './teddyPicture.js'

export type AvatarSize = 'small' | 'medium' | 'large'

export interface AvatarProps {
  teddy: AvatarKey | null
  /** Falls back to an initial when there is no teddy chosen yet. */
  name: string
  size?: AvatarSize
}

const SIZES: Record<AvatarSize, string> = {
  small: 'size-10 text-(length:--text-small)',
  medium: 'size-16 text-(length:--text-heading)',
  large: 'size-28 text-(length:--text-title)',
}

/**
 * A teddy photograph, or a lettered placeholder until one is supplied.
 *
 * The placeholder matters: nothing in the app should be blocked on the camera,
 * and a missing photo should look deliberate rather than broken.
 */
export function Avatar({ teddy, name, size = 'medium' }: AvatarProps) {
  const meta = teddy
  const initial = name.trim().charAt(0).toUpperCase() || '?'

  return (
    <span
      className={[
        'border-border bg-surface-sunken inline-flex shrink-0 items-center justify-center',
        'font-display text-ink-muted overflow-hidden rounded-full border-2',
        SIZES[size],
      ].join(' ')}
    >
      {meta ? (
        <img
          src={avatarPicture(meta)}
          alt={avatarName(meta)}
          className="size-full object-cover"
          // No photograph yet: the drawing stands in, and only if that is
          // missing too does the initial take over.
          onError={(event) => fallBackToDrawing(event.currentTarget, meta)}
        />
      ) : (
        <span aria-hidden="true">{initial}</span>
      )}
    </span>
  )
}
